import { useOutletContext } from 'react-router-dom'

import { DuplicateExpenseCardFragment as ExpensableDuplicate } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { Session } from 'modules/core/hooks/useGetCurrentSession'

import type { Expensable as ExpensableDataTable } from 'modules/expenses/components/expenses/DataTable/types'
import { RouteContextParams, Section } from 'modules/expenses/lib/RouteContextParams'
import { isMyExpensesSection } from 'modules/expenses/lib/common'
import {
  expensableReimbursableCurrency,
  isDraftExpensable,
  isPaidExpensable,
  isReimbursableExpensable,
  isRemovableFromPayrollExpensable,
  isRestartableExpensable,
  isReviewableExpensable,
} from 'modules/expenses/lib/expensables/expensable'
import { Expensable as ExpensableDetail } from 'modules/expenses/types/Expensable'

type Expensable = ExpensableDetail | ExpensableDataTable

export const canRestartExpensable = (
  expensable: Expensable,
  canRestartApprovalFlow: boolean,
  section?: Section
) => isRestartableExpensable(expensable, canRestartApprovalFlow) && !isMyExpensesSection(section)

export const canSetToPaidExpensable = (
  expensable: Expensable,
  canSetExpensableToPaid: boolean,
  section?: Section
) => canSetExpensableToPaid && expensable.status === 'approved' && !isMyExpensesSection(section)

export const canRevertPaidExpensable = (
  expensable: Expensable,
  canSetExpensableToPaid: boolean,
  section?: Section
) =>
  canSetExpensableToPaid &&
  (expensable.reimbursementMethod == 'unknown' ||
    expensable.reimbursementMethod == undefined ||
    expensable.reimbursementMethod == null) &&
  isPaidExpensable(expensable) &&
  !isMyExpensesSection(section)

export const canRemoveExpensableFromPayroll = (expensable: Expensable, canSync: boolean) =>
  isRemovableFromPayrollExpensable(expensable) && canSync

export const canAddExpensableToPayroll = (expensable: Expensable, canSync: boolean) =>
  isReimbursableExpensable(expensable) && canSync

export const canAddExpensableToPayment = (expensable: Expensable, canSync: boolean) =>
  isReimbursableExpensable(expensable) &&
  canSync &&
  expensableReimbursableCurrency(expensable) === 'EUR'

export const canReviewExpensable = (
  expensable: Expensable | ExpensableDuplicate,
  section?: Section
) => isReviewableExpensable(expensable) && !isMyExpensesSection(section)

export const canAddExpensableToGroup = (
  expensableGroupsEnabled: boolean,
  expensable: Expensable,
  section?: Section,
  currentEmployee?: Session['employee']
) =>
  expensableGroupsEnabled &&
  isDraftExpensable(expensable) &&
  !expensable.groupId &&
  currentEmployee?.id === expensable.owner?.id &&
  isMyExpensesSection(section)

export const canRemoveExpensableFromGroup = (expensable: Expensable, section?: Section) =>
  isDraftExpensable(expensable) && expensable.groupId && isMyExpensesSection(section)

export const canAnyExpensableAction = (
  expensableGroupsEnabled: boolean,
  expensable: Expensable,
  canSyncWithPayroll: boolean,
  canSyncWithBanking: boolean,
  canRestartApprovalFlow: boolean,
  canSetExpensableToPaid: boolean,
  section?: Section,
  currentEmployee?: Session['employee']
) =>
  Boolean(
    canReviewExpensable(expensable, section) ||
      canRestartExpensable(expensable, canRestartApprovalFlow, section) ||
      canSetToPaidExpensable(expensable, canSetExpensableToPaid, section) ||
      canRevertPaidExpensable(expensable, canSetExpensableToPaid, section) ||
      canAddExpensableToGroup(expensableGroupsEnabled, expensable, section, currentEmployee) ||
      canRemoveExpensableFromGroup(expensable, section) ||
      canRemoveExpensableFromPayroll(expensable, canSyncWithPayroll) ||
      canAddExpensableToPayroll(expensable, canSyncWithPayroll) ||
      canAddExpensableToPayment(expensable, canSyncWithBanking)
  )

export const useAvailableActions = (
  expensables: Expensable[],
  canRestartApprovalFlow: boolean,
  canSetExpensableToPaid: boolean
) => {
  const { canSyncWithPayroll, canSyncWithBanking, section, expensableGroupsEnabled } =
    useOutletContext<RouteContextParams>()
  const { employee } = useCurrentSession()

  return {
    canReview: expensables.some((expensable) => canReviewExpensable(expensable, section)),
    canRestart: expensables.some((expensable) =>
      canRestartExpensable(expensable, canRestartApprovalFlow, section)
    ),
    canSetToPaid: expensables.some((expensable) =>
      canSetToPaidExpensable(expensable, canSetExpensableToPaid, section)
    ),
    canRevertPaid: expensables.some((expensable) =>
      canRevertPaidExpensable(expensable, canSetExpensableToPaid, section)
    ),
    canAddToGroup: expensables.some((expensable) =>
      canAddExpensableToGroup(expensableGroupsEnabled, expensable, section, employee)
    ),
    canRemoveFromGroup: expensables.some((expensable) =>
      canRemoveExpensableFromGroup(expensable, section)
    ),
    canRemoveFromPayroll: expensables.some((expensable) =>
      canRemoveExpensableFromPayroll(expensable, canSyncWithPayroll)
    ),
    canAddToPayroll: expensables.some((expensable) =>
      canAddExpensableToPayroll(expensable, canSyncWithPayroll)
    ),
    canAddToPayment: expensables.some((expensable) =>
      canAddExpensableToPayment(expensable, canSyncWithBanking)
    ),
  }
}
